import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useDarkMode } from '../../../../context/DarkModeContext';
import MDEditor from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import '@uiw/react-markdown-preview/markdown.css';
import { LightBulbIcon, SparklesIcon, PencilIcon } from '@heroicons/react/24/outline';

interface ReflectionStepProps {
  onNext: (reflections: ReflectionData) => void;
  onBack: () => void;
  initialData?: ReflectionData | null;
}

export interface ReflectionData {
  biggestWin: string;
  keyLearnings: string;
  challenges: string;
  growth: string;
  proudestMoment: string;
  futureGoals: string;
}

const ReflectionStep: React.FC<ReflectionStepProps> = ({ onNext, onBack, initialData }) => {
  const { darkMode } = useDarkMode();
  const [reflections, setReflections] = useState<ReflectionData>(
    initialData || {
      biggestWin: '',
      keyLearnings: '',
      challenges: '',
      growth: '',
      proudestMoment: '',
      futureGoals: ''
    }
  );
  
  const bgColor = darkMode ? 'bg-gray-800' : 'bg-gray-50';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const cardBgColor = darkMode ? 'bg-gray-900' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  
  const prompts = [
    {
      key: 'biggestWin' as keyof ReflectionData,
      title: '🎯 Your Biggest Win',
      prompt: 'What was your most significant achievement during this internship?',
      placeholder: 'Describe a project, task, or moment where you really excelled...'
    },
    {
      key: 'keyLearnings' as keyof ReflectionData,
      title: '📚 Key Learnings',
      prompt: 'What are the top 3-5 things you learned?',
      placeholder: '• Technical skills\n• Professional insights\n• Industry knowledge...'
    },
    {
      key: 'challenges' as keyof ReflectionData,
      title: '🏔️ Challenges Overcome',
      prompt: 'What challenges did you face and how did you overcome them?',
      placeholder: 'Describe a difficult situation and how you navigated it...'
    },
    {
      key: 'growth' as keyof ReflectionData,
      title: '🌱 Personal Growth',
      prompt: 'How have you grown professionally and personally?',
      placeholder: 'Think about skills, confidence, perspectives...'
    },
    {
      key: 'proudestMoment' as keyof ReflectionData,
      title: '⭐ Proudest Moment',
      prompt: 'What moment made you feel most proud?',
      placeholder: 'A specific instance where you felt accomplished...'
    },
    {
      key: 'futureGoals' as keyof ReflectionData,
      title: '🚀 Future Goals',
      prompt: 'Based on this experience, what are your next steps?',
      placeholder: 'Career goals, skills to develop, areas to explore...'
    }
  ];
  
  const handleNext = () => {
    onNext(reflections);
  };
  
  const hasAtLeastOneReflection = Object.values(reflections).some(value => value.trim().length > 0);
  const isComplete = hasAtLeastOneReflection; // Only require at least one reflection
  
  return (
    <div className="space-y-6">
      {/* Introduction */}
      <div className={`${cardBgColor} rounded-lg border ${borderColor} p-6`}>
        <div className="flex items-start gap-3">
          <LightBulbIcon className="w-6 h-6 text-yellow-500 flex-shrink-0" />
          <div>
            <h3 className={`font-semibold mb-2 ${textColor}`}>Take Time to Reflect</h3>
            <p className={`text-sm ${mutedTextColor}`}>
              Before your review, it's important to reflect on your journey. These prompts will help you 
              articulate your experiences and prepare thoughtful talking points. <span className="font-medium">Fill in as many as you'd like - you don't need to complete all of them.</span>
            </p>
          </div>
        </div>
      </div>
      
      {/* Reflection Prompts */}
      <div className="space-y-6">
        {prompts.map((prompt, index) => (
          <motion.div
            key={prompt.key}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`${cardBgColor} rounded-lg border ${borderColor} p-6`}
          >
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className={`font-semibold text-lg ${textColor}`}>{prompt.title}</h4>
                <span className={`text-xs ${mutedTextColor} italic`}>Optional</span>
              </div>
              <p className={`text-sm ${mutedTextColor}`}>{prompt.prompt}</p>
            </div>
            
            <div data-color-mode={darkMode ? 'dark' : 'light'}>
              <MDEditor
                value={reflections[prompt.key]}
                onChange={(value) => setReflections(prev => ({
                  ...prev,
                  [prompt.key]: value || ''
                }))}
                preview="edit"
                hideToolbar={false}
                height={150}
                textareaProps={{
                  placeholder: prompt.placeholder
                }}
              />
            </div>
            
            {reflections[prompt.key].length > 0 && (
              <div className={`mt-2 text-xs ${mutedTextColor}`}>
                {reflections[prompt.key].split(' ').length} words
              </div>
            )}
          </motion.div>
        ))}
      </div>
      
      {/* AI Suggestions */}
      <div className={`${cardBgColor} rounded-lg border ${borderColor} p-6`}>
        <div className="flex items-start gap-3">
          <SparklesIcon className="w-6 h-6 text-purple-500 flex-shrink-0" />
          <div>
            <h4 className={`font-semibold mb-2 ${textColor}`}>Tips for Great Reflections</h4>
            <ul className={`text-sm ${mutedTextColor} space-y-1`}>
              <li>• Be specific - use concrete examples and numbers where possible</li>
              <li>• Show growth - compare where you started vs. where you are now</li>
              <li>• Be authentic - genuine reflections resonate more than perfect answers</li>
              <li>• Connect to impact - explain how your work contributed to the team/company</li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* Progress Indicator */}
      <div className={`${cardBgColor} rounded-lg border ${borderColor} p-4`}>
        <div className="flex items-center justify-between">
          <div className={`text-sm ${mutedTextColor}`}>
            <PencilIcon className="w-4 h-4 inline mr-1" />
            {Object.values(reflections).filter(v => v.trim().length > 0).length} of {prompts.length} reflections complete
          </div>
          <div className={`text-sm font-medium ${isComplete ? 'text-green-500' : mutedTextColor}`}>
            {isComplete ? 'Ready to continue!' : 'Add at least one reflection to proceed'}
          </div>
        </div>
      </div>
      
      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            darkMode
              ? 'bg-gray-700 hover:bg-gray-600 text-white'
              : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
          }`}
        >
          Back to Evidence Review
        </button>
        
        <button
          onClick={handleNext}
          disabled={!isComplete}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            isComplete
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          Generate Talking Points
        </button>
      </div>
    </div>
  );
};

export default ReflectionStep;